import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Download, Calendar as CalIcon, FileSpreadsheet } from "lucide-react";
import BranchMultiSelect from "../components/BranchMultiSelect";
import DatePickerInput from "../components/DatePickerInput";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
              "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const isoDate = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

export default function Reports() {
  const now = new Date();
  const [mode, setMode] = useState("monthly"); // "monthly" | "range"
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [start, setStart] = useState(isoDate(daysAgo(30)));
  const [end, setEnd] = useState(isoDate(now));
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const download = async (fmt = "pdf") => {
    setBusy(true);
    try {
      let params;
      let url;
      let filename;
      const ext = fmt === "xlsx" ? "xlsx" : "pdf";
      if (mode === "monthly") {
        params = { year, month };
        url = `/reports/monthly.${ext}`;
        filename = `report_${year}_${String(month).padStart(2, "0")}.${ext}`;
      } else {
        if (!start || !end) { setBusy(false); return toast.error("Seleziona data inizio e fine"); }
        if (end < start) { setBusy(false); return toast.error("La data fine deve essere >= data inizio"); }
        params = { start, end };
        url = `/reports/range.${ext}`;
        filename = `report_${start}_${end}.${ext}`;
      }
      if (branchIds.length > 0) params.branch_id = branchIds.join(",");
      const res = await api.get(url, { params, responseType: "blob" });
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Report ${fmt.toUpperCase()} generato`);
    } catch (err) {
      const msg = err?.response?.data?.detail || `Errore download ${fmt.toUpperCase()}`;
      toast.error(typeof msg === "string" ? msg : `Errore download ${fmt.toUpperCase()}`);
    } finally { setBusy(false); }
  };

  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

  // Handy quick presets for range mode
  const applyPreset = (days) => {
    setStart(isoDate(daysAgo(days - 1)));
    setEnd(isoDate(new Date()));
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div>
        <span className="pill pill-gold"><FileText className="h-3 w-3" /> REPORT</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Report PDF</h1>
        <p className="text-zinc-400 mt-1">Corrispettivi, scontrini, scontrino medio, vendite per giorno e per fascia oraria. Scegli un mese o un intervallo personalizzato.</p>
      </div>

      {/* Mode toggle */}
      <div className="card-elev p-2 inline-flex gap-1" data-testid="report-mode-toggle">
        <button
          data-testid="report-mode-monthly"
          onClick={() => setMode("monthly")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === "monthly" ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400 hover:text-zinc-100"}`}
        >Mensile</button>
        <button
          data-testid="report-mode-range"
          onClick={() => setMode("range")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${mode === "range" ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400 hover:text-zinc-100"}`}
        ><CalIcon className="h-4 w-4" /> Intervallo personalizzato</button>
      </div>

      {mode === "monthly" ? (
        <div className="card-elev p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Anno</div>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger data-testid="report-year" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Mese</div>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger data-testid="report-month" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Sede</div>
            <BranchMultiSelect
              branches={branches}
              value={branchIds}
              onChange={setBranchIds}
              testId="report-branch"
            />
          </div>
          <Button data-testid="report-download" onClick={() => download("pdf")} disabled={busy} className="h-11 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
            <Download className="h-4 w-4 mr-2" /> {busy ? "..." : "Scarica PDF"}
          </Button>
          <Button data-testid="report-download-xlsx" onClick={() => download("xlsx")} disabled={busy} variant="outline" className="h-11 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
        </div>
      ) : (
        <div className="card-elev p-6 space-y-4" data-testid="report-range-panel">
          <div className="flex flex-wrap gap-2">
            <button data-testid="range-preset-7" onClick={() => applyPreset(7)} className="pill pill-muted text-xs hover:bg-zinc-800">Ultimi 7 giorni</button>
            <button data-testid="range-preset-30" onClick={() => applyPreset(30)} className="pill pill-muted text-xs hover:bg-zinc-800">Ultimi 30 giorni</button>
            <button data-testid="range-preset-90" onClick={() => applyPreset(90)} className="pill pill-muted text-xs hover:bg-zinc-800">Ultimi 90 giorni</button>
            <button data-testid="range-preset-ytd" onClick={() => { setStart(`${now.getFullYear()}-01-01`); setEnd(isoDate(new Date())); }} className="pill pill-muted text-xs hover:bg-zinc-800">Anno corrente</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Data inizio</div>
              <DatePickerInput testid="report-start-date" value={start} onChange={(e) => setStart(e.target.value)} max={end} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Data fine</div>
              <DatePickerInput testid="report-end-date" value={end} onChange={(e) => setEnd(e.target.value)} min={start} max={isoDate(new Date())} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Sede</div>
              <BranchMultiSelect
                branches={branches}
                value={branchIds}
                onChange={setBranchIds}
                testId="report-branch-range"
              />
            </div>
            <div className="flex gap-2">
              <Button data-testid="report-download-range" onClick={() => download("pdf")} disabled={busy} className="h-11 flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                <Download className="h-4 w-4 mr-2" /> PDF
              </Button>
              <Button data-testid="report-download-range-xlsx" onClick={() => download("xlsx")} disabled={busy} variant="outline" className="h-11 flex-1 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-zinc-500" data-testid="report-range-span">
            Intervallo: {start} → {end} ({Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1)} giorni). Massimo 24 mesi.
          </div>
        </div>
      )}

      <div className="card-elev p-5 text-sm text-zinc-400">
        <div className="font-heading font-semibold text-zinc-100 mb-2">Cosa contiene il report</div>
        <ul className="list-disc pl-5 space-y-1 marker:text-zinc-600">
          <li>Corrispettivi totali</li>
          <li>Numero scontrini e scontrino medio</li>
          <li>Numero clienti unici che hanno acquistato</li>
          <li>Punti emessi e riscattati</li>
          <li>Grafico vendite per giorno (aggregato automaticamente per intervalli lunghi)</li>
          <li>Grafico vendite per fascia oraria (0–23)</li>
          <li>Top 8 clienti per spesa</li>
          <li>Statistiche coupon (utilizzati, sconto totale/medio)</li>
        </ul>
      </div>
    </div>
  );
}
